import React from "react";
import { LoadingOverlay } from "@mantine/core";
import { Navigate } from "react-router-dom";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { useSalon } from "../../context/SalonContext";
import { useOwnerOnboardingRedirect } from "../../hooks/useOwnerOnboardingRedirect";

/**
 * Computes default landing destination from access data and redirects.
 * Used when user hits root `/` or unknown path (path="*").
 *
 * Logic matches docs/POST_SIGNIN_REQUEST_INVENTORY.md and routingUtils.calculateRoutingDestination():
 * - Access loading → LoadingOverlay
 * - 402 / payment_required → /admin/settings/subscription
 * - No salon → /account-creation
 * - Non-Owner → /admin/calendar
 * - Initial onboarding incomplete → /account-creation
 * - Progressive onboarding incomplete → /onboarding
 * - Owner of ANOTHER location with incomplete onboarding → switch into it
 *   (DESIGN_DELTA_2026-07-23 §4; not a hard gate — see useOwnerOnboardingRedirect)
 * - All complete → /admin/calendar
 */
export default function LandingRedirect() {
  const { access, isLoading, error } = useFeatureAccess();
  const { isMissingSalon, businessInfo } = useSalon();

  // Owner login redirect to incomplete-location onboarding. Only armed when
  // this user would otherwise land on the calendar as a fully-onboarded Owner
  // of the selected location — every other branch below wins first.
  const selectedLocationComplete =
    access?.onboarding?.initial_onboarding === "complete" &&
    access?.onboarding?.progressive_employees === "complete" &&
    access?.onboarding?.progressive_services === "complete" &&
    access?.onboarding?.progressive_payments === "complete";
  // NOTE: no businessInfo requirement — SalonContext now resolves the salon
  // record only after /access settles, so businessInfo is typically still
  // null when this first renders. The hook needs only /access data
  // (available_locations + selected_salon_id).
  const ownerRedirectEnabled =
    !!access &&
    !error &&
    access.user_level === "Owner" &&
    isMissingSalon !== true &&
    selectedLocationComplete;
  const { status: ownerRedirectStatus } =
    useOwnerOnboardingRedirect(ownerRedirectEnabled);

  // TODO: handle permanent /access failures. When the fetch fails (`error` set,
  // `access` still null) nothing here ever reads `error`, so the user sits on
  // this spinner forever. Render an error state (e.g. Retry / Sign out) instead.
  if (isLoading || !access) {
    return <LoadingOverlay visible />;
  }

  // 402 / payment required
  if (error?.type === "payment_required") {
    return <Navigate to="/admin/settings/subscription" replace />;
  }

  // No salon (new Owner)
  if (isMissingSalon || (!businessInfo && isMissingSalon !== null)) {
    return <Navigate to="/account-creation" replace />;
  }

  // Non-Owner (Manager, Employee, Assistant → calendar)
  if (
    access.user_level === "Manager" ||
    access.user_level === "Employee" ||
    access.user_level === "Assistant"
  ) {
    return <Navigate to="/admin/calendar" replace />;
  }

  // Owner: check onboarding
  const { onboarding } = access;
  if (
    onboarding?.initial_onboarding === "null" ||
    onboarding?.initial_onboarding === "inprogress"
  ) {
    return <Navigate to="/account-creation" replace />;
  }

  const progressiveComplete =
    onboarding?.progressive_employees === "complete" &&
    onboarding?.progressive_services === "complete" &&
    onboarding?.progressive_payments === "complete";

  if (!progressiveComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Fully-onboarded Owner: before landing on the calendar, check whether
  // another location they OWN has incomplete onboarding (flag ON only). Hold
  // the loading overlay until the check resolves to "none" — including the
  // first "idle" render, or <Navigate> would fire before the probe effect
  // runs. A found target ends in a full page reload into sub-flow B.
  if (ownerRedirectEnabled && ownerRedirectStatus !== "none") {
    return <LoadingOverlay visible />;
  }

  return <Navigate to="/admin/calendar" replace />;
}
